/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.initValuesDialog();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled && this.desktopSortBy) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        let sortBy;

        // Keep the mobile facets form sync'd with the desktop sort by dropdown
        if (this.sortingEnabled && evt.target.tagName === 'CUSTOM-SELECT') {
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === evt.detail.selectedValue;
          });
          sortBy = evt.detail.selectedValue;
        }

        this.applyFilters(this.formSearchParams(sortBy), evt);
      }, timeoutDelay);
    }

    /**
     * The filter and sort parameters the form holds now.
     * @param {string} [sortBy] - The sort order, when it is not the form's own.
     * @returns {string} Search parameters, without empty ones.
     */
    formSearchParams(sortBy) {
      const searchParams = new URLSearchParams(new FormData(this.form));
      const emptyParams = [];

      if (this.sortingEnabled) {
        searchParams.set('sort_by', sortBy ?? searchParams.get('sort_by'));
      }

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      return searchParams.toString();
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show all' button clicked: every value, in the dialog.
      const showAll = target.closest('.js-show-more');
      if (showAll) this.openValuesDialog(showAll.closest('.filter'));
    }

    /**
     * Binds the "Show all" dialog (ADR-0028). It is outside the form, so a
     * re-render of the filters leaves it, and its listeners, in place.
     */
    initValuesDialog() {
      this.valuesDialog = this.querySelector('#filter-dialog');
      if (!this.valuesDialog) return;

      const dialog = this.valuesDialog;
      this.valuesList = dialog.querySelector('.filter-dialog__list');
      this.valuesApply = dialog.querySelector('[data-filter-dialog-apply]');

      this.on(dialog, 'click', (evt) => {
        // The dialog is the overlay; its window is the only child.
        if (evt.target === dialog || evt.target.closest('[data-filter-dialog-cancel]')) {
          this.closeValuesDialog();
        } else if (evt.target.closest('[data-filter-dialog-select-all]')) {
          this.setDialogValues(true);
        } else if (evt.target.closest('[data-filter-dialog-clear]')) {
          this.setDialogValues(false);
        } else if (evt.target.closest('[data-filter-dialog-apply]')) {
          this.applyValuesDialog();
        }
      });
      this.on(dialog, 'change', () => this.updateValuesDialog());
      // The dialog owns Escape: on a phone it sits in the filters drawer,
      // which closes on an Escape keyup (SideDrawer).
      this.on(dialog, 'keyup', (evt) => {
        if (evt.key === 'Escape') evt.stopPropagation();
      });
      // Escape: the dialog's own cancel, run through the closing transition.
      this.on(dialog, 'cancel', (evt) => {
        evt.preventDefault();
        this.closeValuesDialog();
      });
    }

    /**
     * Opens the dialog on every value of a filter. The values are copied from
     * the sidebar, which holds them all (those past "Visible options per
     * filter" are only hidden), so they match it in markup, counts, swatches
     * and disabled state; the copies keep a link to the checkbox they mirror.
     * @param {Element} filter - The filter's <details>.
     */
    openValuesDialog(filter) {
      const dialog = this.valuesDialog;
      if (!dialog || dialog.open) return;

      const sourceList = filter.querySelector('.filter__content ul');
      this.valuesSources = [...sourceList.querySelectorAll('input[type="checkbox"]')];

      this.valuesList.className = `filter-dialog__list ${sourceList.className}`.trim();
      this.valuesList.replaceChildren(
        ...this.valuesSources.map((source) => {
          const item = source.closest('li').cloneNode(true);
          item.removeAttribute('class');
          const input = item.querySelector('input');
          input.id = `${source.id}-all`;
          input.removeAttribute('name');
          input.checked = source.checked;
          item.querySelector('label').htmlFor = input.id;
          return item;
        }),
      );

      dialog.querySelector('.filter-dialog__title').textContent = filter.querySelector('summary .flex-auto').textContent.trim();
      this.updateValuesDialog();
      this.valuesList.scrollTop = 0;
      dialog.showModal();
      // Keyboard focus starts on the first value, not on the window.
      this.valuesList.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
    }

    /**
     * Checks or clears every value that can be chosen.
     * @param {boolean} checked
     */
    setDialogValues(checked) {
      this.valuesList.querySelectorAll('input:not(:disabled)').forEach((input) => {
        input.checked = checked;
      });
      this.updateValuesDialog();
    }

    /**
     * "Apply" is only enabled when the choice differs from the sidebar's.
     */
    updateValuesDialog() {
      const inputs = this.valuesList.querySelectorAll('input');
      const changed = [...inputs].some((input, i) => input.checked !== this.valuesSources[i].checked);
      this.valuesApply.disabled = !changed;
    }

    /**
     * Sets the dialog's choice in the sidebar and filters once, as soon as the
     * dialog has closed: focus is back on "Show all", whose id the re-render
     * keeps (applyFilters restores it).
     */
    async applyValuesDialog() {
      this.valuesList.querySelectorAll('input').forEach((input, i) => {
        this.valuesSources[i].checked = input.checked;
      });
      await this.closeValuesDialog();
      this.applyFilters(this.formSearchParams(), null);
    }

    /**
     * Closes the dialog after its closing transition (modal.css), however long
     * the theme or a reduced-motion setting makes it.
     * @returns {Promise<void>}
     */
    async closeValuesDialog() {
      const dialog = this.valuesDialog;
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      await Promise.allSettled(dialog.getAnimations({ subtree: true }).map((animation) => animation.finished));
      dialog.classList.remove('is-closing');
      dialog.close();
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal,
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(getComputedStyle(this.parentElement).getPropertyValue('--header-height').replace('px', ''), 10) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth',
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into.
          // Dispatched on `document` (global broadcast); bubbles is irrelevant
          // on document and intentionally omitted to avoid the ADR-0001 H-12
          // signature foot-gun where options after CustomEvent get dropped.
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'));
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
